import React, { useState, useEffect } from 'react';
import withRoleAccess from '../../hoc/withRoleAccess';
import {
  Card,
  Row,
  Col,
  DatePicker,
  Button,
  Statistic,
  Spin,
  Alert,
  Typography,
  Space,
  Tag,
  Progress,
  Table,
  Badge,
  message
} from 'antd';
import {
  BookOutlined,
  ReloadOutlined,
  CalendarOutlined,
  FilePdfOutlined,
  SwapOutlined,
  InfoCircleOutlined,
  BarChartOutlined,
  UserOutlined,
  UsergroupAddOutlined,
  LockOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  TrophyOutlined
} from '@ant-design/icons';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const API_URL = process.env.REACT_APP_API_URL;

const ClassReport = () => {
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(30, 'day'),
    dayjs()
  ]);
  
  const [summaryData, setSummaryData] = useState(null);
  const [changesData, setChangesData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Real API calls using fetch
  const fetchSummaryData = async () => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/report/class-reports/summary?from=${dateRange[0].format('YYYY-MM-DD')}&to=${dateRange[1].format('YYYY-MM-DD')}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Failed to fetch class summary');
    return await response.json();
  };

  const fetchChangesData = async () => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/report/class-reports/changes?from=${dateRange[0].format('YYYY-MM-DD')}&to=${dateRange[1].format('YYYY-MM-DD')}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Failed to fetch class changes');
    return await response.json();
  };

  const loadData = async () => {
    if (!API_URL) {
      setError('API URL is not configured. Please set REACT_APP_API_URL in your environment variables.');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setError('Authentication token not found. Please log in again.');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const [summary, changes] = await Promise.all([
        fetchSummaryData(),
        fetchChangesData()
      ]);
      
      setSummaryData(summary);
      setChangesData(changes);
      
      message.success('Class report data loaded successfully');
    } catch (err) {
      console.error('Error loading class report data:', err);
      
      let errorMessage = 'Failed to load class report data';
      if (err.status) {
        if (err.status === 401) {
          errorMessage = 'Authentication failed. Please log in again.';
        } else if (err.status === 400) {
          errorMessage = 'Invalid request parameters';
        } else {
          errorMessage = `Server error: ${err.status}`;
        }
      } else {
        errorMessage = err.message || 'An unexpected error occurred';
      }
      
      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dateRange && dateRange.length === 2 && dateRange[0] && dateRange[1]) {
      loadData();
    }
  }, [dateRange]);

  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      setDateRange(dates);
    }
  };

  const handleManualRefresh = () => {
    loadData();
  };

  const handlePrintPDF = () => {
    // Open a new window for printing
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    
    // Get the content to print
    const reportContainer = document.getElementById('class-report-container');
    const printContent = reportContainer ? reportContainer.innerHTML : '';
    
    // Create the print document
    const printDocument = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Class Analytics Report</title>
          <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/antd/5.0.0/reset.css">
          <style>
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              margin: 0;
              padding: 20px;
              background: white;
            }
            .ant-card { 
              box-shadow: none !important; 
              border: 1px solid #d9d9d9 !important;
              margin-bottom: 16px;
              page-break-inside: avoid;
            }
            .ant-statistic-title { font-weight: bold !important; }
            .ant-statistic-content { font-size: 18px !important; }
            .ant-card-head { 
              border-bottom: 2px solid #1890ff !important; 
              background: #f0f2f5 !important;
            }
            .ant-card-head-title { 
              font-weight: bold !important; 
              color: #1890ff !important; 
            }
            .print-header { 
              display: block !important;
              text-align: center; 
              margin-bottom: 20px; 
              border-bottom: 3px solid #1890ff; 
              padding-bottom: 10px; 
            }
            .print-date-range { 
              display: block !important;
              text-align: center; 
              margin-bottom: 15px; 
              font-weight: bold; 
            }
            svg { max-width: 100% !important; height: auto !important; }
            .no-print { display: none !important; }
            @page { 
              margin: 15mm; 
              size: A4; 
            }
          </style>
        </head>
        <body>
          ${printContent}
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              };
            };
          </script>
        </body>
      </html>
    `;
    
    // Write the document and trigger print
    printWindow.document.write(printDocument);
    printWindow.document.close();
  };

  // Prepare chart data
  const subjectsChartData = summaryData?.top_subjects?.map(subject => ({
    name: subject.subject_name?.length > 20 ? subject.subject_name.substring(0, 20) + '...' : subject.subject_name,
    count: parseInt(subject.count) || 0
  })) || [];

  // Privacy distribution data
  const privacyData = summaryData?.privacy_distribution?.map(privacy => ({
    name: privacy.type || 'Unknown',
    count: parseInt(privacy.count) || 0,
    percentage: parseFloat(privacy.percentage) || 0
  })) || [];

  // Change reasons chart data
  const changeReasonsChartData = changesData?.change_reason_summary?.map(reason => ({
    name: reason.changereason || 'Unspecified',
    count: parseInt(reason.count) || 0
  })) || [];

  // Most changed classes data
  const mostChangedClassesData = changesData?.most_changed_classes || [];

  // Privacy colors and icons mapping
  const privacyColors = {
    'private': '#fa8c16',
    'group': '#52c41a',
    'Unknown': '#8c8c8c'
  };

  const privacyIcons = {
    'private': <UserOutlined />,
    'group': <UsergroupAddOutlined />,
    'Unknown': <InfoCircleOutlined />
  };

  const privacyDisplayNames = {
    'private': 'Private Classes',
    'group': 'Group Classes',
    'Unknown': 'Unknown'
  };


  // Table columns for recent class changes
  const recentChangesColumns = [
    {
      title: 'Class Code',
      dataIndex: 'class_code',
      key: 'class_code',
      render: (code) => <Tag color="blue">{code}</Tag>
    },
    {
      title: 'Subject',
      dataIndex: 'subject_name',
      key: 'subject_name',
      render: (subject) => (
        <Space>
          <BookOutlined />
          {subject}
        </Space>
      )
    },
    {
      title: 'Teacher',
      dataIndex: 'teacher_name',
      key: 'teacher_name',
      render: (name, record) => record.nickname || name
    },
    {
      title: 'Date Change',
      key: 'date_change',
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <Text type="secondary">{dayjs(record.originaldate).format('MMM DD')}</Text>
          <Text strong style={{ color: '#f5222d' }}>
            → {dayjs(record.newdate).format('MMM DD')}
          </Text>
        </Space>
      )
    },
    {
      title: 'Reason',
      dataIndex: 'changereason',
      key: 'changereason',
      render: (reason) => (
        <Tag color="orange">{reason || 'Not specified'}</Tag>
      )
    },
    {
      title: 'Changed',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (date) => (
        <Text type="secondary">
          {dayjs(date).format('MMM DD, HH:mm')}
        </Text>
      )
    }
  ];

  // Table columns for most changed classes
  const mostChangedColumns = [
    {
      title: 'Rank',
      key: 'rank',
      render: (_, __, index) => (
        <Badge 
          count={index + 1} 
          style={{ 
            backgroundColor: index === 0 ? '#faad14' : index === 1 ? '#d9d9d9' : index === 2 ? '#cd7f32' : '#1890ff' 
          }}
        />
      ),
      width: 80,
    },
    {
      title: 'Class Code',
      dataIndex: 'class_code',
      key: 'class_code',
      render: (code) => <Tag color="blue">{code}</Tag>
    },
    {
      title: 'Subject',
      dataIndex: 'subject_name',
      key: 'subject_name',
    },
    {
      title: 'Teacher',
      dataIndex: 'teacher_name',
      key: 'teacher_name',
    },
    {
      title: 'Changes',
      dataIndex: 'change_count',
      key: 'change_count',
      render: (count) => (
        <Space>
          <SwapOutlined style={{ color: '#f5222d' }} />
          <Text strong style={{ color: '#f5222d' }}>{count}</Text>
        </Space>
      ),
      sorter: (a, b) => b.change_count - a.change_count,
    }
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold">{`${label}`}</p>
          <p className="text-blue-600">
            {`Count: ${payload[0].value}`}
          </p>
        </div>
      );
    }
    return null;
  };

  const PieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const displayName = privacyDisplayNames[payload[0].name] || payload[0].name;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold">{displayName}</p>
          <p className="text-blue-600">
            {`Count: ${payload[0].value} (${payload[0].payload.percentage}%)`}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <div id="class-report-container" style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Print Header - Only visible when printing */}
        <div className="print-header" style={{ display: 'none' }}>
          <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
            Class Analytics Report
          </Title>
          <Text>Generated on {new Date().toLocaleDateString()}</Text>
        </div>

        {/* Print Date Range - Only visible when printing */}
        <div className="print-date-range" style={{ display: 'none' }}>
          Report Period: {dateRange[0]?.format('YYYY-MM-DD')} to {dateRange[1]?.format('YYYY-MM-DD')}
        </div>

        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <Title level={2} style={{ marginBottom: '8px' }}>
            Class Analytics Report
          </Title>
          <Text type="secondary">
            Comprehensive overview of class distribution, privacy settings, and schedule changes
          </Text>
        </div>

        {/* Date Range and Controls */}
        <Card style={{ marginBottom: '24px' }}>
          <Space size="large" wrap>
            <Space>
              <CalendarOutlined />
              <Text strong>Date Range:</Text>
              <RangePicker
                value={dateRange}
                onChange={handleDateRangeChange}
                format="YYYY-MM-DD"
                allowClear={false}
              />
            </Space>
            <Space>
              <Button 
                type="primary" 
                icon={<ReloadOutlined />}
                onClick={handleManualRefresh}
                loading={loading}
              >
                Update Report
              </Button>
              <Button 
                type="default" 
                icon={<FilePdfOutlined />}
                onClick={handlePrintPDF}
                disabled={!summaryData && !changesData}
              >
                Print to PDF
              </Button>
            </Space>
          </Space>
        </Card>

        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: '24px' }}
          />
        )}

        <Spin spinning={loading}>
          {/* Key Metrics */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total Classes"
                  value={summaryData?.total_classes || 0}
                  prefix={<BookOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Subject Varieties"
                  value={summaryData?.top_subjects?.length || 0}
                  prefix={<BarChartOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total Changes"
                  value={changesData?.logs?.length || 0}
                  prefix={<SwapOutlined />}
                  valueStyle={{ color: '#fa8c16' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Change Reasons"
                  value={changesData?.change_reason_summary?.length || 0}
                  prefix={<ExclamationCircleOutlined />}
                  valueStyle={{ color: '#f5222d' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Charts Row */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            {/* Most Taught Subjects */}
            <Col xs={24} lg={12}>
              <Card 
                title={
                  <Space>
                    <BookOutlined style={{ color: '#1890ff' }} />
                    Most Taught Subjects
                  </Space>
                }
              >
                {subjectsChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={subjectsChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                      <YAxis />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Bar dataKey="count" fill="#1890ff" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <Text type="secondary">No subject data available</Text>
                  </div>
                )}
              </Card>
            </Col>

            {/* Change Reasons Distribution */}
            <Col xs={24} lg={12}>
              <Card 
                title={
                  <Space>
                    <ExclamationCircleOutlined style={{ color: '#f5222d' }} />
                    Change Reasons
                  </Space>
                }
              >
                {changeReasonsChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={changeReasonsChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                      <YAxis />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Bar dataKey="count" fill="#f5222d" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <Text type="secondary">No change reason data available</Text>
                  </div>
                )}
              </Card>
            </Col>
          </Row>

          {/* Privacy Distribution */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} lg={12}>
              <Card 
                title={
                  <Space>
                    <LockOutlined style={{ color: '#52c41a' }} />
                    Class Privacy Distribution
                  </Space>
                }
              >
                {privacyData.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={privacyData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percentage }) => `${privacyDisplayNames[name] || name} (${percentage}%)`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="count"
                        >
                          {privacyData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={privacyColors[entry.name] || '#8c8c8c'} />
                          ))}
                        </Pie>
                        <RechartsTooltip content={<PieTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{ textAlign: 'center', marginTop: '16px' }}>
                      <Space size="large" wrap>
                        {privacyData.map((entry) => (
                          <Space key={entry.name}>
                            {privacyIcons[entry.name]}
                            <div 
                              style={{ 
                                width: '12px', 
                                height: '12px', 
                                backgroundColor: privacyColors[entry.name] || '#8c8c8c',
                                borderRadius: '50%' 
                              }}
                            />
                            <Text>{privacyDisplayNames[entry.name] || entry.name}: {entry.count}</Text>
                          </Space>
                        ))}
                      </Space>
                    </div>
                  </>
                ) : (
                  <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <Text type="secondary">No privacy data available</Text>
                  </div>
                )}
              </Card>
            </Col>

            {/* Privacy Progress Bars */}
            <Col xs={24} lg={12}>
              <Card title="Class Privacy Breakdown">
                {privacyData.length > 0 ? (
                  <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    {privacyData.map((privacy) => (
                      <div key={privacy.name}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <Space>
                            {privacyIcons[privacy.name]}
                            <Text strong>{privacyDisplayNames[privacy.name] || privacy.name}</Text>
                          </Space>
                          <Text>{privacy.count} classes</Text>
                        </div>
                        <Progress 
                          percent={privacy.percentage} 
                          strokeColor={privacyColors[privacy.name] || '#8c8c8c'}
                          format={(percent) => `${percent}%`}
                        />
                      </div>
                    ))}
                  </Space>
                ) : (
                  <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <Text type="secondary">No privacy data available</Text>
                  </div>
                )}
              </Card>
            </Col>
          </Row>

          {/* Most Changed Classes */}
          {mostChangedClassesData.length > 0 && (
            <Card 
              title={
                <Space>
                  <TrophyOutlined style={{ color: '#faad14' }} />
                  Most Changed Classes
                </Space>
              }
              style={{ marginBottom: '24px' }}
            >
              <Table
                columns={mostChangedColumns}
                dataSource={mostChangedClassesData}
                rowKey="class_code"
                pagination={false}
                size="middle"
                bordered
              />
            </Card>
          )}

        </Spin>

        <style jsx>{`
          .print-header, .print-date-range {
            display: none;
          }
          
          @media print {
            .print-header, .print-date-range {
              display: block !important;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default withRoleAccess(ClassReport, ['superadmin']);